import type { estypes } from '@elastic/elasticsearch'
import { z } from 'zod'

import type { SearchTypeFromMappingType } from '../mapping'
import { SuperSource } from '../super'

export type SearchResponse<T> = {
  nbPages: number
  page: number
  hits: T[]
  profile?: estypes.SearchProfile
}

export type SerializedSearchObjectMetadata = {
  objectID: string
  title: string
  type: 'rich_text' | 'discussion' | 'collection'
  noteId: string
  channelId: string
  createdBy: string
  createdAt: Date
  updatedAt: Date
  archivedAt: Date | null
  iconShape: string | null
  iconVariant: string | null
  iconColor: string | null
  isArchived: boolean
  isTemplate: boolean
  organizationId: string
  _tags: string[]
  restrictedTo: string[]
  parentNotesIdList: string[]
  parentNotesTitleList: string[]
  fullPath: string
  isChannelDoc: boolean
  hasChildren: boolean
  coverImageUrl: string | null | undefined
  isCollectionRoot: boolean
  writerIds: string[]
  channelType: 'public' | 'private' | 'personal' | 'public_read_only'
  channelPurpose: 'regular' | 'templates' | 'discussions' | 'external_sources'
  isPubliclyShared: boolean
  reviewState:
    | null
    | 'none'
    | 'pending_review'
    | 'final'
    | 'out_of_date'
    | 'verification_requested'
  lastEditedAt: null | Date
  lastEditedBy: string[]
  ownerId: string
}

export type UserSuperSeat = 'assigned' | 'pending' | 'unassigned'

export type SerializedUserSearchObject = {
  objectID: string
  kind: 'human' | 'service_account'
  displayName: string
  username: string
  email: string
  photoURL: null | string
  organizationRole: string
  organizationId: string
  updatedAt: string
  createdAt: string
  lastActivityAt: null | string
  isArchived: boolean
  invitePending: null | boolean
  superSeat: UserSuperSeat
  _tags: string[]
  description: string
}

export type SerializedUserGroupSearchObject = {
  objectID: string
  name: string
  description: string
  _tags: string[]
  organizationId: string
  memberIdList: string[]
}

export enum GenericMappingType {
  KEYWORD = 'keyword',
  DATE = 'date',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
}

export type GenericMapping = {
  key: string
  name?: string
  description?: string
  type: GenericMappingType
  enum?: readonly string[]
  enumFromCollapseEsQuery?: boolean
  enumFromTermsAggEsQuery?: boolean
  disabled?: boolean
  /** Marks fields intended for aggregate or record views. */
  analytics?: boolean
}

type RequireLiteral<T> = T extends GenericMappingType
  ? [GenericMappingType] extends [T]
    ? never
    : T
  : never

export type GenericMappingByKey = Record<
  string,
  Omit<GenericMapping, 'key' | 'type'> & {
    order: number
    type: RequireLiteral<GenericMappingType>
  }
>

type GenericMappingTypeToValueType = {
  [GenericMappingType.KEYWORD]: string | string[]
  [GenericMappingType.DATE]: Date | string
  [GenericMappingType.NUMBER]: number
  [GenericMappingType.BOOLEAN]: boolean
}

type ExtractValueType<T> = T extends { type: infer U }
  ? U extends GenericMappingType
    ? GenericMappingTypeToValueType[U]
    : never
  : never

export type TypedGenericMetadata<T extends GenericMappingByKey> = {
  [K in keyof T]: Array<ExtractValueType<T[K]> | undefined>
}

export type LooselyTypedGenericMetadata<T extends readonly GenericMapping[]> = {
  [K in T[number]['key']]?: Array<
    | GenericMappingTypeToValueType[Extract<T[number], { key: K }>['type']]
    | undefined
  >
}

export type MetadataValue =
  | GenericMappingTypeToValueType[GenericMappingType]
  | undefined

export type SearchObjectFieldsTypeFromMappingObjectType<
  T extends Record<string, estypes.MappingProperty>,
> = {
  [K in keyof T]?: K extends 'organizationId'
    ? string
    : SearchTypeFromMappingType<T[K]['type']>
} & { organizationId: string }

export type SearchResult<T> = {
  [K in keyof estypes.SearchHit<T>]: K extends 'fields'
    ? {
        [K2 in keyof T]?: T[K2][]
      }
    : estypes.SearchHit<T>[K]
}

export type SearchErrorResponse = {
  id: string
  message: string
  status: number
}

export enum SuperAutomationDestinationType {
  EMAIL = 'email',
  SLACK = 'slack',
}

export type TestAutomationConfig = {
  userId: string
  title: string
  destinationType: SuperAutomationDestinationType
}

const AudienceScopeSchema = z
  .object({
    sourceTypes: z.array(z.enum(SuperSource)).optional(),
    excludeSourceTypes: z.array(z.enum(SuperSource)).optional(),
    // Node IDs match the node itself, its root, and its descendants.
    nodeIds: z.array(z.string()).optional(),
    excludeNodeIds: z.array(z.string()).optional(),
    // Dates are ISO strings or ES date math ('now-1M').
    updatedAfter: z.string().optional(),
    updatedBefore: z.string().optional(),
    authorEmails: z.array(z.string()).optional(),
    // Review state only filters Slite documents.
    reviewState: z.string().optional(),
  })
  .strict()

/** Run-wide retrieval filters; source and node picks are combined with OR. */
export type AudienceScope = z.infer<typeof AudienceScopeSchema>

export const AudienceObjectSchema = z
  .object({
    mirroredAudience: z.array(z.string()),
    // Restricts matches to these root IDs; an empty list matches nothing.
    accessibleRootIds: z.array(z.string()).optional(),
    // Scopes are intersected, so appending one can only narrow retrieval.
    scopes: z.array(AudienceScopeSchema).optional(),
  })
  .strict()

/**
 * An audience matches if at least one of the audience string is in the
 * document's audience, and none of the audience string that start with "s:u:"
 * or "s:g:" is in the document's restrictedTo.
 *
 * It also needs the document rootId to be in the rootIds list if the rootIds list is provided.
 */
export type AudienceObject = z.infer<typeof AudienceObjectSchema>

export type SuperOrganization = {
  id: string
  name: string
  domain: string
  superProduct: null | string
  product: string
  featureFlags: string[]
  // Org default model profile id (MODEL_PROFILES), or null.
  superModelProfile: string | null
  /**
   * Org-level general context prompt (Settings → Slite AI), appended
   * server-side to ask prompts. Empty string when unset.
   */
  superGeneralContext: string
  /** Billing-transparency setting: the org sees what a run cost in credits. */
  showCreditCost: boolean
  metadata: {
    trialStep: string | null
    superSubscriptionStatus: string | null
    trialEndsAt: Date | null
    trialStepEndsAt: Date | null
    trialMode: string | null
    /**
     * Recency-decay floor for the org (oldest of its content signals). Computed
     * server-side; travels here instead of the search-token claim so the search
     * service reads it from the resolved org. JSON-transported as an ISO string
     * (like the trial dates above) — parse before doing date math.
     */
    decayTargetDate: Date
  }
}

export type SuperUser = {
  id: string
  displayName: string
  email: string
  featureFlags: string[]
  // The user's own model profile id (MODEL_PROFILES), overriding the org's;
  // absent from an api deployed before the field.
  superModelProfile?: string | null
}

export type AudienceUserAndOrganization = {
  audience: AudienceObject
  organization: SuperOrganization
  user: SuperUser
}

export type GenerateSuperDigestInput = {
  organizationId: string
  recipientUserId: string
  recipientUserDisplayName: string
  parentIdList?: string[]
  sourceTypeList?: string[]
  updatedAfter?: string
  updatedBefore?: string
  customPrompt?: string
  contextPrompt?: string
  destinationType: SuperAutomationDestinationType
  automationId?: string
  superAssistantId?: string
  testAutomation?: TestAutomationConfig
  workflow?: Record<string, unknown> | null
}
